import { useMutation, type UseMutationOptions, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import type { CreateShiftBody, UpdateShiftBody } from '@okvc/schema';

import { ApiClient } from './api.client.js';

export interface ShiftOccurrence {
  id: string;
  userId: string;
  start: string;
  end: string;
  ruleId: string;
  isRecurring: boolean;
}

export interface ShiftSummaryItem {
  userId: string;
  totalMinutes: number;
}

export const ShiftsApiKeys = {
  GET_SHIFTS: 'GET_SHIFTS',
  GET_SHIFTS_SUMMARY: 'GET_SHIFTS_SUMMARY',
  CREATE_SHIFT: 'CREATE_SHIFT',
  UPDATE_SHIFT: 'UPDATE_SHIFT',
  DELETE_SHIFT: 'DELETE_SHIFT',
};

export class ShiftsApi extends ApiClient {
  public static getShifts(startDate?: string, endDate?: string): Promise<ShiftOccurrence[]> {
    return this.http.get('/shifts', { params: { startDate, endDate } }).then((res) => res.data);
  }

  public static createShift(body: CreateShiftBody): Promise<{ rule: { id: string }; overlapWarning: boolean }> {
    return this.http.post('/shifts', body).then((res) => res.data);
  }

  public static updateShift(occurrenceId: string, body: UpdateShiftBody): Promise<unknown> {
    return this.http.patch(`/shifts/${occurrenceId}`, body).then((res) => res.data);
  }

  public static deleteShift(occurrenceId: string, scope: 'this' | 'following'): Promise<void> {
    return this.http.delete(`/shifts/${occurrenceId}`, { params: { scope } }).then((res) => res.data);
  }

  public static getSummary(startDate?: string, endDate?: string): Promise<ShiftSummaryItem[]> {
    return this.http.get('/shifts/summary', { params: { startDate, endDate } }).then((res) => res.data);
  }
}

export function useShifts(startDate?: string, endDate?: string, queryOptions?: UseQueryOptions<ShiftOccurrence[]>) {
  return useQuery<ShiftOccurrence[]>(
    [ShiftsApiKeys.GET_SHIFTS, startDate, endDate],
    () => ShiftsApi.getShifts(startDate, endDate),
    queryOptions,
  );
}

export function useCreateShift(
  mutationOptions?: UseMutationOptions<{ rule: { id: string }; overlapWarning: boolean }, unknown, CreateShiftBody>,
) {
  const queryClient = useQueryClient();
  return useMutation<{ rule: { id: string }; overlapWarning: boolean }, unknown, CreateShiftBody>(
    [ShiftsApiKeys.CREATE_SHIFT],
    (body) => ShiftsApi.createShift(body),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([ShiftsApiKeys.GET_SHIFTS]);
      },
      ...mutationOptions,
    },
  );
}

export function useUpdateShift(
  mutationOptions?: UseMutationOptions<unknown, unknown, { occurrenceId: string } & UpdateShiftBody>,
) {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, { occurrenceId: string } & UpdateShiftBody>(
    [ShiftsApiKeys.UPDATE_SHIFT],
    ({ occurrenceId, ...body }) => ShiftsApi.updateShift(occurrenceId, body),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([ShiftsApiKeys.GET_SHIFTS]);
      },
      ...mutationOptions,
    },
  );
}

export function useDeleteShift(
  mutationOptions?: UseMutationOptions<void, unknown, { occurrenceId: string; scope: 'this' | 'following' }>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, { occurrenceId: string; scope: 'this' | 'following' }>(
    [ShiftsApiKeys.DELETE_SHIFT],
    ({ occurrenceId, scope }) => ShiftsApi.deleteShift(occurrenceId, scope),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([ShiftsApiKeys.GET_SHIFTS]);
      },
      ...mutationOptions,
    },
  );
}

export function useShiftsSummary(startDate?: string, endDate?: string) {
  return useQuery<ShiftSummaryItem[]>(
    [ShiftsApiKeys.GET_SHIFTS_SUMMARY, startDate, endDate],
    () => ShiftsApi.getSummary(startDate, endDate),
    { enabled: !!startDate && !!endDate },
  );
}
